<template>
  <banners>
    <div
      v-if="!loading"
      class="mx-auto max-w-screen-lg px-4 md:px-10"
    >
      <div class="mt-5 flex items-center justify-between">
        <h1 class="text-left text-2xl text-dark-blue">My Carriers</h1>
        <button-main
          v-if="activeEmptyState"
          variant="secondary"
          size="md"
          class="group flex max-w-max items-center justify-self-end text-1lg text-gray-over-dark"
          @click="$router.push('/dispatcher/add-carriers')"
        >
          Add a Carrier
          <svg
            class="ml-2 h-5 w-5 fill-current text-gray-over-dark group-hover:text-body-color group-focus:text-dark-gray"
            viewBox="0 0 24 30"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 23L12 7"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              d="M20 15L4 15"
              stroke="currentColor"
              stroke-width="3"
            />
          </svg>
        </button-main>
      </div>
      <div class="mb-5 grid grid-cols-2 items-center">
        <form-field
          v-if="connectedCarriers.length || search_input"
          id="search_input"
          :value="search_input"
          placeholder="Search Connected Carriers"
          :on-input="onInput"
          name="search_input"
          search-icon
          class="hidden text-dark-blue md:block"
        />
      </div>
      <p class="text-left text-1md text-body-color">
        Here are the carriers your Dispatch Service company is authorized to
        onboard and represent.
      </p>
      <form-field
        v-if="connectedCarriers.length || search_input"
        id="search_input"
        :value="search_input"
        placeholder="Search Connected Carriers"
        :on-input="onInput"
        name="search_input"
        search-icon
        class="block text-dark-blue md:hidden"
      />
      <div v-if="pendingAccessRequests.length">
        <my-carriers-subheader
          title="Pending Access Requests"
          :show-button="!!pendingAccessRequests.length"
        />
      </div>
      <section v-if="pendingAccessRequests.length">
        <div
          v-for="(request, index) in pendingAccessRequests"
          :key="index"
        >
          <pending-request-card :request="request" />
        </div>
      </section>
      <section v-if="connectedCarriers.length">
        <hr
          v-if="pendingAccessRequests.length"
          class="my-5 border-t-2 border-light-gray"
        />
        <my-carriers-subheader
          title="Connected Carriers"
          :show-button="!pendingAccessRequests.length"
        />
        <div
          v-for="(connectedCarrier, index) in connectedCarriers"
          :key="index"
        >
          <connected-carrier-card
            :connected-carrier="connectedCarrier"
            @delete-connection="showDeleteModal"
          />
        </div>
      </section>
      <section v-if="activeEmptyState">
        <div class="my-3 rounded-md bg-off-white p-4">
          <small class="text-left text-1md text-body-color">
            No results found
          </small>
        </div>
      </section>
    </div>
    <confirm-delete-modal
      v-if="confirmDelete"
      subtext="Removing this connection will prevent you from being able to onboard or act on behalf of this carrier."
      :visible="confirmDelete"
      @close="closeDeleteModal"
      @delete="handleDeleteCarrierConnection"
    />
  </banners>
</template>
<script>
import Banners from '@/components/Banners.vue';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import PendingRequestCard from '@/views/cards/PendingRequestCard.vue';
import ConnectedCarrierCard from '@/views/cards/ConnectedCarrierCard.vue';
import ConfirmDeleteModal from '@/components/modals/ConfirmDeleteModal.vue';
import MyCarriersSubheader from './MyCarriersSubheader.vue';
import { toastResponse } from '@/common/handleServerResponse';

export default {
  name: 'DispatcherDashboard',
  components: {
    Banners,
    FormField,
    ConfirmDeleteModal,
    PendingRequestCard,
    MyCarriersSubheader,
    ConnectedCarrierCard,
    ButtonMain,
  },
  data() {
    return {
      confirmDelete: false,
      connectionToDelete: 0,
      filter_value: '',
      loading: false,
    };
  },
  emits: ['cancel'],
  computed: {
    pendingAccessRequests() {
      const pendingRequests =
        this.$store.state.dispatcherConnections.connections.filter(
          (carrierConection) => carrierConection.status === 'pending'
        );
      this.sortDispatcherConnections(pendingRequests);
      return pendingRequests;
    },
    activeEmptyState() {
      return (
        this.connectedCarriers?.length === 0 &&
        this.pendingAccessRequests?.length === 0 &&
        this.connectedCarriers?.length === 0
      );
    },
    connectedCarriers() {
      let connectedConnections =
        this.$store.state.dispatcherConnections.connections.filter(
          (carrierConection) => carrierConection.status === 'active'
        );
      if (this.filter_value) {
        const uppercase_value = this.filter_value.toUpperCase();
        connectedConnections =
          this.$store.state.dispatcherConnections.connections.filter(
            (carrierConection) => {
              return (
                carrierConection.status === 'active' &&
                carrierConection.carrier.legal_name.includes(uppercase_value)
              );
            }
          );
      }
      this.sortDispatcherConnections(connectedConnections);
      return connectedConnections;
    },
  },
  async created() {
    this.loading = true;
    await this.$store.dispatch('dispatcherConnections/getListOfCarriers');
    this.loading = false;
  },
  mounted() {
    this.removeRedirectAfterIDV();
  },
  methods: {
    removeRedirectAfterIDV() {
      localStorage?.removeItem('redirectAfterIDV');
    },
    cancelEdit() {
      this.$emit('cancel');
    },
    onInput(e) {
      const value_to_search = e.target.value;
      this.search_input = value_to_search;
      this.filter_value = value_to_search;
    },
    showDeleteModal(connectionId) {
      this.connectionToDelete = connectionId;
      this.confirmDelete = true;
    },
    closeDeleteModal() {
      this.confirmDelete = false;
    },
    async handleDeleteCarrierConnection() {
      try {
        const res = await this.$store.dispatch(
          'dispatcherConnections/inactivateDispatcherConnection',
          this.connectionToDelete
        );
        toastResponse(this, res, 'The carrier connection has been deleted');
        await this.$store.dispatch('dispatcherConnections/getListOfCarriers');
      } catch (error) {
        console.log('ERROR: ', error);
      } finally {
        this.confirmDelete = false;
      }
    },
    async sortDispatcherConnections(connections) {
      // TODO This will be changing for a backend fucntion -> Clark is in charge.
      connections.sort((a, b) => {
        let fa = a.carrier.legal_name.toLowerCase(),
          fb = b.carrier.legal_name.toLowerCase();

        if (fa < fb) {
          return -1;
        }
        if (fa > fb) {
          return 1;
        }
        return 0;
      });
    },
  },
};
</script>
<style scoped></style>
